import Button from "../components/ds/Button";
import Modal from "../components/ds/Modal";
import DiscoverCampaignCard from "../components/discover/DiscoverCampaignCard";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FaTiktok, FaInstagram, FaFacebookF, FaYoutube, FaInfoCircle } from "react-icons/fa";
import { FiUpload, FiX, FiPlus } from "react-icons/fi";
import {
  formatCampaignNumber,
} from "../utils/campaignCardUtils";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { createCampaign, updateCampaign, getCampaign } from "../api/campaigns";
import { uploadCampaignLogo, uploadCampaignAttachmentFile } from "../api/upload";
import { createAnnouncement } from "../api/announcements";
import { refusedUploadMessage } from "../utils/refusedUpload";

const CreateCampaignPage = ({ isDark, setToast }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const spaceId = searchParams.get("spaceId");
  const isEditMode = !!id;
  const [step, setStep] = useState(1);
  const [slideDirection, setSlideDirection] = useState("next");
  const [isAnimating, setIsAnimating] = useState(false);
  const [showClipperTooltip, setShowClipperTooltip] = useState(false);
  const [clipperTooltipPosition, setClipperTooltipPosition] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pendingCampaignData, setPendingCampaignData] = useState(null);
  const additionalAssetsFileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    campaignName: "",
    campaignThumbnail: null,
    discoverMediaType: "image",
    visibility: "public",
    projectDescription: "",
    campaignType: "ugc", // "ugc", "clipping", or "both" - replaces participantType
    contentGuidelines: "",
    
    // Requirements - dynamic array
    requirements: [""], // Array of requirement strings
    
    // Platforms
    platforms: {
      tiktok: false,
      instagramReels: false,
      facebookReels: false,
      youtubeShorts: false,
      youtubeLongForm: false,
    },
    
    // Per-platform rates
    perPlatformRates: {
      tiktok: "",
      instagramReels: "",
      facebookReels: "",
      youtubeShorts: "",
      youtubeLongForm: "",
    },
    usePerPlatformRates: false, // Toggle for per-platform vs general rate
    
    // Assets
    additionalAssets: {
      productPhotos: false,
      promoVideos: false,
      googleDriveLink: false,
      brandGuidelinesPDF: false,
    },
    additionalAssetFiles: [],
    existingAttachmentUrls: [],
    resourceLinks: [""],

    // Payment
    cpmRate: 0.1, // Default rate (used if per-platform not set)
    viewsPerRate: 1000, // Locked to 1000 views
    
    // Payout Limits
    enableMaxPayout: false,
    maxPayout: "",
    
    // Duration
    startDate: "",
    endDate: "",
    runsUntilBudgetSpent: false, // New option
    
    // Budget
    budget: "",
    
    // Post to announcements
    postToAnnouncements: false,
  });

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const glassCard = "border-separator-default bg-bg-raised";
  const cardBase = glassCard;
  const inputBase =
    "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";
  const inputError = "border-status-danger bg-fill-tertiary text-label-primary";
  const buttonBase = "border border-separator-default bg-bg-raised hover:bg-fill-quaternary";
  const errorText = "text-status-danger";

  const optionClass = (selected) =>
    `rounded-radius-md px-space-16 py-space-12 text-left text-subheadline-semibold transition-colors ${
      selected
        ? "bg-app-creator-rewards-deep text-app-creator-rewards-soft"
        : "bg-fill-secondary text-label-primary hover:bg-fill-tertiary"
    }`;

  /** Figma's choice buttons: Gray M, Tinted in the app's colours when chosen. */
  const chipClass = (selected) =>
    `inline-flex h-[40px] items-center justify-center rounded-[22px] px-space-16 text-caps-button-m uppercase transition-colors ${
      selected
        ? "bg-app-creator-rewards-deep text-app-creator-rewards-soft"
        : "bg-fill-secondary text-label-primary hover:bg-fill-tertiary"
    }`;

  const wizardSteps = [
    { n: 1, label: t("createCampaignWizard.stepBasics") || "Basics" },
    { n: 2, label: t("createCampaignWizard.stepBudget") || "Budget & rates" },
    { n: 3, label: t("createCampaignWizard.stepAssets") || "Assets" },
    { n: 4, label: t("createCampaignWizard.stepSchedule") || "Schedule" },
    { n: 5, label: t("createCampaignWizard.stepLaunch") || "Launch" },
  ];

  const getSlideStyle = (stepNum) => {
    const isActive = stepNum === step;
    const isPrev = stepNum < step;
    const isNext = stepNum > step;
    
    if (isAnimating) {
      if (slideDirection === "next") {
        // Only show current (sliding out) and next (sliding in)
        if (isActive) {
          return {
            transform: "translateX(-100%)",
            opacity: 0,
            transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
            position: "absolute",
            width: "100%",
            pointerEvents: "none",
          };
        } else if (isNext && stepNum === step + 1) {
          return {
            transform: "translateX(0)",
            opacity: 1,
            transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
            position: "absolute",
            width: "100%",
            pointerEvents: "none",
          };
        } else {
          // Hide all other steps during animation
          return {
            display: "none",
          };
        }
      } else {
        // Previous direction
        if (isActive) {
          return {
            transform: "translateX(100%)",
            opacity: 0,
            transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
            position: "absolute",
            width: "100%",
            pointerEvents: "none",
          };
        } else if (isPrev && stepNum === step - 1) {
          return {
            transform: "translateX(0)",
            opacity: 1,
            transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
            position: "absolute",
            width: "100%",
            pointerEvents: "none",
          };
        } else {
          // Hide all other steps during animation
          return {
            display: "none",
          };
        }
      }
    }
    
    // Normal state - only show active step
    return {
      transform: isActive ? "translateX(0)" : isPrev ? "translateX(-100%)" : "translateX(100%)",
      opacity: isActive ? 1 : 0,
      transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
      position: isActive ? "relative" : "absolute",
      width: "100%",
      pointerEvents: isActive ? "auto" : "none",
      display: isActive ? "block" : "none",
    };
  };

  // Validation functions for each step
  const validateStep = (stepNum) => {
    const newErrors = {};

    switch (stepNum) {
      case 1:
        // Step 1: Basic info + requirements + platforms
        if (!formData.campaignName.trim()) {
          newErrors.campaignName = t("createCampaignWizard.validation.campaignNameRequired");
        }
        if (!formData.projectDescription.trim()) {
          newErrors.projectDescription = t("createCampaignWizard.validation.projectDescriptionRequired");
        }
        if (!formData.contentGuidelines.trim()) {
          newErrors.contentGuidelines = t("createCampaignWizard.validation.contentGuidelinesRequired");
        }
        {
          const platformValues = Object.values(formData.platforms);
          const hasPlatform = platformValues.some((val) => val === true);
          if (!hasPlatform) {
            newErrors.platforms = t("createCampaignWizard.validation.platformRequired");
          }
        }
        break;

      case 2:
        // Step 2: Budget, Payment Rate, Min/Max Payouts
        {
        const platformValues = Object.values(formData.platforms);
        const hasPlatform = platformValues.some((val) => val === true);
        // Validate budget
        if (!formData.budget || parseFloat(formData.budget) < 500) {
          newErrors.budget = t("createCampaignWizard.validation.budgetMin");
        }
        // Validate rates based on selection
        const selectedPlatformCount = platformValues.filter(Boolean).length;
        if (hasPlatform && selectedPlatformCount > 1) {
          // Multiple platforms - validate based on toggle
          if (formData.usePerPlatformRates) {
            // Validate per-platform rates
            const selectedPlatforms = Object.entries(formData.platforms)
              .filter(([_, selected]) => selected)
              .map(([platform]) => platform);
            
            for (const platform of selectedPlatforms) {
              const rate = formData.perPlatformRates[platform];
              if (!rate || parseFloat(rate) < 0.1) {
                newErrors.perPlatformRates = newErrors.perPlatformRates || {};
                newErrors.perPlatformRates[platform] = t("createCampaignWizard.validation.rateMin");
              }
            }
          } else {
            // Validate general rate
            if (!formData.cpmRate || formData.cpmRate < 0.1) {
              newErrors.cpmRate = t("createCampaignWizard.validation.paymentRateMin");
            }
          }
        } else if (hasPlatform && selectedPlatformCount === 1) {
          // Single platform - validate general rate
          if (!formData.cpmRate || formData.cpmRate < 0.1) {
            newErrors.cpmRate = t("createCampaignWizard.validation.paymentRateMin");
          }
        }
        
        // Validate Max Payout (optional, but validate if enabled)
        if (formData.enableMaxPayout && (!formData.maxPayout || parseFloat(formData.maxPayout) <= 0)) {
          newErrors.maxPayout = t("createCampaignWizard.validation.maxPayoutPositive");
        }
        }
        break;

      case 3:
        // Step 3: Assets (optional, no validation needed)
        break;

      case 4:
        // Step 4: Duration
        if (!formData.startDate) {
          newErrors.startDate = t("createCampaignWizard.validation.startDateRequired");
        }
        if (!formData.runsUntilBudgetSpent && !formData.endDate) {
          newErrors.endDate = t("createCampaignWizard.validation.endDateRequired");
        }
        if (formData.startDate && formData.endDate && !formData.runsUntilBudgetSpent) {
          const start = new Date(formData.startDate);
          const end = new Date(formData.endDate);
          if (end <= start) {
            newErrors.endDate = t("createCampaignWizard.validation.endAfterStart");
          }
        }
        break;

      case 5:
        // Step 5: Announcements (optional)
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (!validateStep(step)) {
      if (setToast) {
        setToast(t("toasts.pleaseFillRequiredFields"));
      }
      return;
    }

    if (step < 5) {
      setIsAnimating(true);
      setSlideDirection("next");
      setTimeout(() => {
        setStep(step + 1);
        setIsAnimating(false);
        // Clear errors when moving to next step
        setErrors({});
      }, 300);
    }
  };

  const handlePrev = () => {
    if (step > 1) {
      setIsAnimating(true);
      setSlideDirection("prev");
      setTimeout(() => {
        setStep(step - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  // Load campaign data when editing
  useEffect(() => {
    const loadCampaign = async () => {
      if (!isEditMode || !id) {
        setIsLoading(false);
        return;
      }

      // Reset form data when id changes
      setFormData({
        campaignName: "",
        campaignThumbnail: null,
        projectDescription: "",
        campaignType: "ugc",
        contentGuidelines: "",
        requirements: [""],
        platforms: {
          tiktok: false,
          instagramReels: false,
          facebookReels: false,
          youtubeShorts: false,
          youtubeLongForm: false,
        },
        budget: "",
        cpmRate: 0.1,
        viewsPerRate: 1000,
        usePerPlatformRates: false,
        perPlatformRates: {},
        enableMaxPayout: false,
        maxPayout: "",
        startDate: "",
        endDate: "",
        runsUntilBudgetSpent: false,
        postToAnnouncements: false,
        additionalAssets: {
          productPhotos: false,
          promoVideos: false,
          googleDriveLink: false,
          brandGuidelinesPDF: false,
        },
        additionalAssetFiles: [],
        existingAttachmentUrls: [],
        resourceLinks: [""],
      });
      setErrors({});
      setStep(1);

      try {
        setIsLoading(true);
        const response = await getCampaign(id);
        if (response.success && response.data) {
          const campaign = response.data;
          
          // Format dates for input fields
          const formatDateForInput = (dateString) => {
            if (!dateString) return "";
            const date = new Date(dateString);
            return date.toISOString().split("T")[0];
          };

          setFormData({
            campaignName: campaign.campaignName || "",
            discoverMediaType: campaign.thumbnailType || "image",
            visibility: campaign.visibility === "members_only" ? "members_only" : "public",
            campaignThumbnail: campaign.thumbnail || campaign.campaignLogo || null,
            projectDescription: campaign.projectDescription || "",
            campaignType: campaign.participantType === "clippers" ? "clipping" : campaign.participantType === "both" ? "both" : "ugc",
            contentGuidelines: campaign.contentGuidelines || "",
            requirements: campaign.requirements && campaign.requirements.length > 0 
              ? campaign.requirements 
              : [""],
            platforms: campaign.platforms || {
              tiktok: false,
              instagramReels: false,
              facebookReels: false,
              youtubeShorts: false,
              youtubeLongForm: false,
            },
            budget: campaign.budget ? String(campaign.budget) : "",
            cpmRate: campaign.cpmRate || 0.1,
            viewsPerRate: 1000,
            usePerPlatformRates: false,
            perPlatformRates: campaign.perPlatformRates || {},
            enableMaxPayout: campaign.enableMaxPayout || false,
            maxPayout: campaign.maxPayout ? String(campaign.maxPayout) : "",
            startDate: formatDateForInput(campaign.startDate),
            endDate: formatDateForInput(campaign.endDate),
            runsUntilBudgetSpent: campaign.noEndDate || false,
            postToAnnouncements: false, // Always false for edit mode
            additionalAssets: campaign.additionalAssets || {
              productPhotos: false,
              promoVideos: false,
              googleDriveLink: false,
              brandGuidelinesPDF: false,
            },
            additionalAssetFiles: [],
            existingAttachmentUrls: Array.isArray(campaign.additionalAssetFileUrls) ? campaign.additionalAssetFileUrls : [],
            resourceLinks:
              Array.isArray(campaign.resourceLinks) && campaign.resourceLinks.length > 0
                ? campaign.resourceLinks
                : [""],
          });
        } else {
          if (setToast) {
            setToast(t("campaigns.campaignNotFound"));
          }
          navigate("/");
        }
      } catch (error) {
        console.error("Error loading campaign:", error);
        if (setToast) {
          setToast(t("campaigns.failedToLoadCampaign"));
        }
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaign();
  }, [id, isEditMode, navigate, setToast, t]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePlatformChange = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platform]: !prev.platforms[platform],
      },
    }));
  };

  const handleAdditionalAssetChange = (asset) => {
    setFormData(prev => ({
      ...prev,
      additionalAssets: {
        ...prev.additionalAssets,
        [asset]: !prev.additionalAssets[asset],
      },
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, campaignThumbnail: file }));
    }
  };

  const handleAdditionalFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      additionalAssetFiles: [...(prev.additionalAssetFiles || []), ...files],
    }));
    if (additionalAssetsFileInputRef.current) {
      additionalAssetsFileInputRef.current.value = "";
    }
  };

  const handleRemovePendingFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      additionalAssetFiles: (prev.additionalAssetFiles || []).filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      existingAttachmentUrls: (prev.existingAttachmentUrls || []).filter((_, i) => i !== index),
    }));
  };

  const prepareCampaignData = async () => {
    let campaignThumbnailUrl = "";
    
    // Upload campaign thumbnail if there's a new file
    if (formData.campaignThumbnail && formData.campaignThumbnail instanceof File) {
      try {
        const uploadResponse = await uploadCampaignLogo(formData.campaignThumbnail);
        if (uploadResponse.success) {
          campaignThumbnailUrl = uploadResponse.url;
        }
      } catch (error) {
        console.error("Failed to upload campaign thumbnail:", error);
        throw new Error(refusedUploadMessage(error) || t("toasts.failedUploadThumbnail"));
      }
    } else if (typeof formData.campaignThumbnail === "string") {
      // If it's already a URL (from edit mode), use it
      campaignThumbnailUrl = formData.campaignThumbnail;
    }

    const uploadedAttachmentUrls = [];
    for (const f of formData.additionalAssetFiles || []) {
      if (f instanceof File) {
        const up = await uploadCampaignAttachmentFile(f);
        if (up.success && up.url) uploadedAttachmentUrls.push(up.url);
      }
    }
    const additionalAssetFileUrls = [...(formData.existingAttachmentUrls || []), ...uploadedAttachmentUrls];
    const resourceLinks = (formData.resourceLinks || []).map((l) => String(l).trim()).filter(Boolean);

    // Determine payment rates based on platform count and toggle
    const selectedPlatformCount = Object.values(formData.platforms).filter(Boolean).length;
    let paymentRates = {};
    
    if (selectedPlatformCount > 1 && formData.usePerPlatformRates) {
      // Multiple platforms with per-platform rates enabled
      Object.entries(formData.platforms).forEach(([platform, selected]) => {
        if (selected && formData.perPlatformRates[platform]) {
          paymentRates[platform] = parseFloat(formData.perPlatformRates[platform]);
        }
      });
    } else {
      // Single platform OR multiple platforms with general rate - use cpmRate for all selected platforms
      Object.entries(formData.platforms).forEach(([platform, selected]) => {
        if (selected) {
          paymentRates[platform] = parseFloat(formData.cpmRate);
        }
      });
    }

    return {
      campaignName: formData.campaignName,
      campaignLogo:
        formData.discoverMediaType === "image" ? campaignThumbnailUrl : "",
      thumbnail: campaignThumbnailUrl,
      thumbnailType: formData.discoverMediaType === "video" ? "video" : "image",
      visibility: formData.visibility === "members_only" ? "members_only" : "public",
      projectDescription: formData.projectDescription,
      participantType: formData.campaignType === "ugc" ? "creators" : formData.campaignType === "clipping" ? "clippers" : "both", // Map campaignType to participantType
      contentGuidelines: formData.contentGuidelines,
      requirements: formData.requirements.filter(req => req.trim() !== ""), // Filter out empty requirements
      platforms: formData.platforms,
      perPlatformRates: paymentRates, // New field for per-platform rates
      cpmRate: parseFloat(formData.cpmRate), // Default/fallback rate
      viewsPerRate: 1000, // Locked to 1000 views
      additionalAssets: formData.additionalAssets,
      additionalAssetsFile: additionalAssetFileUrls[0] || "",
      additionalAssetFileUrls,
      resourceLinks,
      enableMaxPayout: formData.enableMaxPayout,
      maxPayout: formData.enableMaxPayout && formData.maxPayout ? parseFloat(formData.maxPayout) : null,
      startDate: formData.startDate,
      endDate: formData.runsUntilBudgetSpent ? null : formData.endDate,
      noEndDate: !!formData.runsUntilBudgetSpent,
      budget: formData.budget ? parseFloat(formData.budget) : 0,
      mintSpace: spaceId || null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isEditMode) {
      // For edit mode, submit immediately
      setIsSubmitting(true);
      try {
        const campaignData = await prepareCampaignData();
        const response = await updateCampaign(id, campaignData);
        if (response.success) {
          if (setToast) {
            setToast(t("campaigns.campaignUpdated"));
          }
          navigate(`/campaigns/${id}`);
        }
      } catch (error) {
        console.error("Error updating campaign:", error);
        const errorMessage = error.response?.data?.message || error.message || t("toasts.failedUpdateCampaign");
        if (setToast) {
          setToast(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // For create mode, show confirmation modal first
      try {
        const campaignData = await prepareCampaignData();
      setPendingCampaignData(campaignData);
      setShowConfirmationModal(true);
      } catch (error) {
        console.error("Error preparing campaign data:", error);
        const errorMessage = error.message || t("toasts.failedPrepareCampaign");
        if (setToast) {
          setToast(errorMessage);
        }
      }
    }
  };

  const handleConfirmCreate = async () => {
    if (!pendingCampaignData) return;
    
    setIsSubmitting(true);
    setShowConfirmationModal(false);
    try {
      const response = await createCampaign(pendingCampaignData);
      if (response.success) {
        // Post to announcements if enabled
        if (formData.postToAnnouncements && spaceId && response.data?._id) {
          try {
            const campaignId = response.data._id;
            await createAnnouncement(
              spaceId,
              t("createCampaignWizard.newCampaignAnnouncement", { name: formData.campaignName }),
              "",
              "",
              campaignId
            );
          } catch (error) {
            console.error("Error posting to announcements:", error);
            // Don't fail the campaign creation if announcement fails
          }
        }
        setShowSuccessModal(true);
        setPendingCampaignData(null);
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
      const errorMessage = error.response?.data?.message || t("toasts.failedCreateCampaign");
      if (setToast) {
        setToast(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateExamplePayout = (views) => {
    let payout = (views / formData.viewsPerRate) * formData.cpmRate;
    if (formData.enableMaxPayout && formData.maxPayout) {
      payout = Math.min(payout, parseFloat(formData.maxPayout));
    }
    return `${payout.toFixed(2)} ₾`;
  };

  // Preview component — matches discover card layout
  /** The real Discover card, so the preview can't drift from what creators see. */
  const CampaignPreview = () => {
    const media =
      formData.campaignThumbnail instanceof File
        ? URL.createObjectURL(formData.campaignThumbnail)
        : formData.campaignThumbnail || "";
    const isVideo =
      formData.discoverMediaType === "video" &&
      Boolean(media) &&
      (formData.campaignThumbnail instanceof File ? formData.campaignThumbnail.type?.startsWith("video/") : true);
    const participantType =
      formData.campaignType === "ugc" ? "creators" : formData.campaignType === "clipping" ? "clippers" : "both";

    return (
      <div className="pointer-events-none" aria-hidden="true">
        <DiscoverCampaignCard
          campaign={{
            ...formData,
            campaignName: formData.campaignName || t("campaigns.campaignName"),
            participantType,
            thumbnail: media,
            thumbnailType: isVideo ? "video" : "image",
            budget: Number(formData.budget) || 0,
            spent: 0,
            status: "pending",
            createdBy: user,
            startDate: formData.startDate || new Date(),
          }}
          onOpen={() => {}}
          t={t}
        />
      </div>
    );
  };

  const totalSteps = 5; // 1: main + requirements + platforms; 2: budget/rates; 3: files; 4: duration; 5: announcements
  const isLastStep = step === totalSteps;

  if (isLoading) {
    return (
      <div className="w-full">
        <div className={`flex items-center justify-center py-12 rounded-radius-lg border backdrop-blur ${cardBase}`}>
          <p className={mutedText}>{t("createCampaignWizard.loadingCampaign")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Hero + progress */}
      <div className="mb-space-24 flex shrink-0 flex-col gap-space-24">
        <div className="flex flex-wrap items-end justify-between gap-space-8">
          <div className="flex min-w-0 flex-col gap-space-4">
            <span className="inline-flex h-[20px] w-fit items-center rounded-radius-sm bg-app-creator-rewards-deep px-[6px] text-caps-badge uppercase text-app-creator-rewards-soft">
              {t("createCampaignWizard.heroBadge") || "Creator Rewards"}
            </span>
            <h1 className="text-title-1 text-label-primary">
              {isEditMode
                ? t("campaigns.editCampaign")
                : `${t("createCampaignWizard.heroTitle") || "Launch your"} ${t("createCampaignWizard.heroAccent") || "campaign"}`}
            </h1>
          </div>
          <span className="text-footnote-semibold text-label-secondary">
            {t("campaigns.step")} {step} {t("campaigns.of")} {totalSteps}
          </span>
        </div>

        <div className="scrollbar-hide flex gap-space-8 overflow-x-auto">
          {wizardSteps.map(({ n, label }) => (
            <span
              key={n}
              className={`inline-flex shrink-0 items-center gap-space-8 rounded-[22px] px-space-12 py-space-8 text-caption-1-semibold ${
                step === n ? "bg-app-creator-rewards-deep text-app-creator-rewards-soft" : "bg-fill-quaternary text-label-tertiary"
              }`}
            >
              <span
                className={`flex h-[18px] w-[20px] items-center justify-center rounded-radius-sm ${
                  step >= n ? "bg-app-creator-rewards-base text-app-creator-rewards-on" : "bg-fill-secondary text-label-secondary"
                }`}
              >
                {n}
              </span>
              {label}
            </span>
          ))}
        </div>

        <div className="h-[6px] overflow-hidden rounded-full bg-fill-secondary">
          <div className="h-full rounded-full bg-app-creator-rewards-base transition-all duration-500" style={{ width: `${(step / totalSteps) * 100}%` }} />
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-space-24 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-h-0 flex-1 flex-col">
      <form
        className={`relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-radius-lg bg-bg-raised p-space-24`}
        onSubmit={(e) => {
          e.preventDefault();
          if (isLastStep) {
            handleSubmit(e);
          }
        }}
        noValidate
      >
        <div className="min-h-0 flex-1 overflow-y-auto pr-0.5">
        {/* Step 1: Campaign Name, Thumbnail, Description, Type, Content Guidelines */}
        <div style={getSlideStyle(1)}>
          <div className="space-y-6">
            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-footnote-semibold text-label-secondary">{t("campaigns.campaignName")}</span>
                <input
                  type="text"
                  name="campaignName"
                  value={formData.campaignName}
                  onChange={handleChange}
                  placeholder={t("createCampaignWizard.campaignNamePlaceholder")}
                      className={`w-full rounded-[22px] border px-space-16 py-[10px] text-callout outline-none transition ${
                    errors.campaignName ? inputError : inputBase
                  }`}
                />
                {errors.campaignName && (
                  <p className={`text-subheadline ${errorText}`}>{errors.campaignName}</p>
                )}
              </label>
            </div>

            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("campaigns.campaignThumbnail")}</span>
                <p className={`text-subheadline ${mutedText}`}>{t("createCampaignWizard.discoverMediaHint")}</p>
                <div className="mb-2 flex flex-wrap gap-2">
                  {["image", "video"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, discoverMediaType: type, campaignThumbnail: null }))}
                      className={chipClass(formData.discoverMediaType === type)}
                    >
                      {t(`createCampaignWizard.discoverMedia_${type}`)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept={formData.discoverMediaType === "video" ? "video/*" : "image/*"}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const isVideo = file.type.startsWith("video/");
                          setFormData((prev) => ({
                            ...prev,
                            campaignThumbnail: file,
                            discoverMediaType: isVideo ? "video" : prev.discoverMediaType,
                          }));
                        }
                      }}
                      className="hidden"
                    />
                    <div className={`px-6 py-3 rounded-radius-md border-2 border-dashed transition ${
                      formData.campaignThumbnail 
                        ? "border-accent-default bg-accent-tinted" 
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}>
                      <span className={baseText}>
                        {formData.campaignThumbnail ? t("campaigns.changeThumbnail") : t("campaigns.uploadThumbnail")}
                      </span>
                    </div>
                  </label>
                  {formData.campaignThumbnail && (
                    <div className="relative w-32 aspect-video rounded-radius-xs overflow-hidden">
                      {formData.discoverMediaType === "video" &&
                      !(formData.campaignThumbnail instanceof File && !formData.campaignThumbnail.type?.startsWith("video/")) &&
                      (typeof formData.campaignThumbnail !== "string" || formData.campaignThumbnail.match(/\.(mp4|webm|mov)/i)) ? (
                        <video
                          src={
                            formData.campaignThumbnail instanceof File
                              ? URL.createObjectURL(formData.campaignThumbnail)
                              : formData.campaignThumbnail
                          }
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                        />
                      ) : (
                        <img
                          src={
                            formData.campaignThumbnail instanceof File
                              ? URL.createObjectURL(formData.campaignThumbnail)
                              : formData.campaignThumbnail
                          }
                          alt={t("createCampaignWizard.thumbnailAlt")}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  )}
                </div>
              </label>
            </div>

            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("createCampaignWizard.visibility")}</span>
                <p className={`text-subheadline ${mutedText}`}>{t("createCampaignWizard.visibilityHint")}</p>
                <div className="flex flex-wrap gap-space-8">
                  {[
                    { id: "public", label: t("createCampaignWizard.visibilityPublic") },
                    { id: "members_only", label: t("createCampaignWizard.visibilityMembersOnly") },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, visibility: opt.id }))}
                      className={chipClass(formData.visibility === opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </label>
            </div>

            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("campaigns.shortProjectDescription")}</span>
                <textarea
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleChange}
                  rows="3"
                  placeholder={t("createCampaignWizard.projectDescriptionPlaceholder")}
                  className={`w-full rounded-radius-md px-4 py-3 text-base outline-none transition resize-none ${
                    errors.projectDescription ? inputError : inputBase
                  }`}
                />
                {errors.projectDescription && (
                  <p className={`text-subheadline ${errorText}`}>{errors.projectDescription}</p>
                )}
              </label>
            </div>

            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("campaigns.type")}</span>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, campaignType: "ugc" }))}
                    className={optionClass(formData.campaignType === "ugc")}
                  >
                    <span className={baseText}>{t("campaigns.ugc")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, campaignType: "clipping" }))}
                    className={optionClass(formData.campaignType === "clipping")}
                  >
                    <span className={baseText}>{t("campaigns.clipping")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, campaignType: "both" }))}
                    className={optionClass(formData.campaignType === "both")}
                  >
                    <span className={baseText}>{t("campaigns.both")}</span>
                  </button>
                </div>
              </label>
            </div>

            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-footnote-semibold text-label-secondary">{t("campaigns.contentGuidelines")}</span>
                <textarea
                  name="contentGuidelines"
                  value={formData.contentGuidelines}
                  onChange={handleChange}
                  rows="5"
                  placeholder={t("createCampaignWizard.contentGuidelinesPlaceholder")}
                  className={`w-full rounded-radius-md px-4 py-3 text-base outline-none transition resize-none ${
                    errors.contentGuidelines ? inputError : inputBase
                  }`}
                />
                {errors.contentGuidelines && (
                  <p className={`text-subheadline ${errorText}`}>{errors.contentGuidelines}</p>
                )}
              </label>
            </div>

            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-footnote-semibold text-label-secondary">{t("campaigns.requirements")}</span>
                <p className={`text-subheadline ${mutedText}`}>Add requirements for creators (e.g., hashtags, tagging, link in bio)</p>
                <div className="space-y-3">
                  {formData.requirements.map((requirement, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={requirement}
                        onChange={(e) => {
                          const newRequirements = [...formData.requirements];
                          newRequirements[index] = e.target.value;
                          setFormData((prev) => ({ ...prev, requirements: newRequirements }));
                        }}
                        placeholder={`Requirement ${index + 1}`}
                        className={`flex-1 rounded-radius-md px-4 py-3 text-base outline-none transition ${inputBase}`}
                      />
                      {formData.requirements.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newRequirements = formData.requirements.filter((_, i) => i !== index);
                            setFormData((prev) => ({ ...prev, requirements: newRequirements }));
                          }}
                          className={`p-3 rounded-radius-md transition ${
                            "text-label-secondary hover:bg-fill-quaternary hover:text-label-primary"
                          }`}
                        >
                          <FiX className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, requirements: [...prev.requirements, ""] }));
                    }}
                    className={`w-full flex items-center justify-center gap-2 rounded-radius-md border-2 px-4 py-3 transition ${
                      "border-separator-default bg-bg-raised hover:bg-fill-quaternary text-label-secondary"
                    }`}
                  >
                    <FiPlus className="h-5 w-5" />
                    <span className="font-medium">{t("campaigns.addRequirement")}</span>
                  </button>
                </div>
              </label>
            </div>

            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("campaigns.whereCanContentBePosted")}</span>
                <p className={`text-subheadline ${mutedText} flex items-center gap-2`}>
                  <FaInfoCircle className="h-4 w-4" />
                  Creators can only submit content from the platforms you select.
                </p>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("tiktok")}
                    className={`flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                      formData.platforms.tiktok
                        ? "border-accent-default bg-accent-tinted"
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}
                  >
                    <FaTiktok className="h-5 w-5" />
                    <span className={baseText}>{t("campaigns.tiktok")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("instagramReels")}
                    className={`flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                      formData.platforms.instagramReels
                        ? "border-accent-default bg-accent-tinted"
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}
                  >
                    <FaInstagram className="h-5 w-5" />
                    <span className={baseText}>{t("campaigns.instagramReels")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("facebookReels")}
                    className={`flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                      formData.platforms.facebookReels
                        ? "border-accent-default bg-accent-tinted"
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}
                  >
                    <FaFacebookF className="h-5 w-5" />
                    <span className={baseText}>{t("campaigns.facebookReels")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("youtubeShorts")}
                    className={`flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                      formData.platforms.youtubeShorts
                        ? "border-accent-default bg-accent-tinted"
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}
                  >
                    <FaYoutube className="h-5 w-5" />
                    <span className={baseText}>{t("campaigns.youtubeShorts")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("youtubeLongForm")}
                    className={`flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                      formData.platforms.youtubeLongForm
                        ? "border-accent-default bg-accent-tinted"
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}
                  >
                    <FaYoutube className="h-5 w-5" />
                    <span className={baseText}>{t("campaigns.youtubeLongForm")}</span>
                  </button>
                </div>
                {errors.platforms && (
                  <p className={`text-subheadline ${errorText}`}>{errors.platforms}</p>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Step 2: Budget, rates & payouts (was part of old step 3) */}
        <div style={getSlideStyle(2)}>
          <div className="space-y-6">
            {/* Budget */}
            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("campaigns.budget")}</span>
                <p className={`text-subheadline ${mutedText}`}>Total amount allocated for this campaign (minimum 500 ₾)</p>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  placeholder="500"
                  min="500"
                  step="0.01"
                  className={`w-full rounded-radius-md px-4 py-3 text-base outline-none transition ${
                    errors.budget ? inputError : inputBase
                  }`}
                />
                {errors.budget && (
                  <p className={`text-subheadline ${errorText}`}>{errors.budget}</p>
                )}
              </label>
            </div>

            {/* Max payout */}
            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("createCampaignWizard.maximumPayout")}</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, enableMaxPayout: !prev.enableMaxPayout }))}
                    className={`w-full flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                      formData.enableMaxPayout
                        ? "border-accent-default bg-accent-tinted"
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      formData.enableMaxPayout 
                        ? "border-accent-default bg-accent-default" 
                        : "border-separator-opaque"
                    }`}>
                      {formData.enableMaxPayout && (
                        <svg className="w-3 h-3 text-accent-on" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className={baseText}>{t("common.enable")}</span>
                  </button>
                  {formData.enableMaxPayout && (
                    <div className="mt-3">
                      <input
                        type="number"
                        name="maxPayout"
                        value={formData.maxPayout}
                        onChange={handleChange}
                        placeholder="100"
                        min="0"
                        step="0.01"
                        className={`w-full rounded-radius-md px-4 py-3 text-base outline-none transition ${
                          errors.maxPayout ? inputError : inputBase
                        }`}
                      />
                      {errors.maxPayout && (
                        <p className={`text-subheadline ${errorText}`}>{errors.maxPayout}</p>
                      )}
                    </div>
                  )}
                </label>
            </div>

            {/* Payment Rate */}
            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("createCampaignWizard.paymentRate")}</span>
                <p className={`text-subheadline ${mutedText}`}>{t("createCampaignWizard.rateLineXPer1k")}</p>
                
                {/* Individual Rates Toggle - Only show if 2+ platforms selected */}
                {Object.values(formData.platforms).filter(Boolean).length > 1 && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, usePerPlatformRates: !prev.usePerPlatformRates }))}
                    className={`w-full flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                      formData.usePerPlatformRates
                        ? "border-accent-default bg-accent-tinted"
                        : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      formData.usePerPlatformRates 
                        ? "border-accent-default bg-accent-default" 
                        : "border-separator-opaque"
                    }`}>
                      {formData.usePerPlatformRates && (
                        <svg className="w-3 h-3 text-accent-on" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className={baseText}>{t("campaigns.individualRates")}</span>
                  </button>
                )}

                {/* General Rate Input - Show when single platform OR when individual rates not selected */}
                {(!formData.usePerPlatformRates || Object.values(formData.platforms).filter(Boolean).length === 1) && (
                  <div className="mt-3">
                    <input
                      type="number"
                      name="cpmRate"
                      value={formData.cpmRate}
                      onChange={handleChange}
                      placeholder="0.1"
                      min="0.1"
                      step="0.1"
                      className={`w-full rounded-radius-md px-4 py-3 text-base outline-none transition ${
                        errors.cpmRate ? inputError : inputBase
                      }`}
                    />
                    {errors.cpmRate && (
                      <p className={`text-subheadline ${errorText}`}>{errors.cpmRate}</p>
                    )}
                  </div>
                )}

                {/* Per-Platform Rates Inputs - Show when individual rates is enabled and 2+ platforms */}
                {formData.usePerPlatformRates && Object.values(formData.platforms).filter(Boolean).length > 1 && (
                  <div className="mt-3 space-y-3">
                    <p className={`text-subheadline ${mutedText}`}>{t("createCampaignWizard.perPlatformRatesHint")}</p>
                    {formData.platforms.tiktok && (
                      <div className="flex items-center gap-3">
                        <FaTiktok className="h-5 w-5 flex-shrink-0" />
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={formData.perPlatformRates.tiktok}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            perPlatformRates: { ...prev.perPlatformRates, tiktok: e.target.value }
                          }))}
                          placeholder="0.1"
                          className={`flex-1 rounded-radius-md px-4 py-3 text-base outline-none transition ${
                            errors.perPlatformRates?.tiktok ? inputError : inputBase
                          }`}
                        />
                        <span className={`text-subheadline ${mutedText} whitespace-nowrap`}>{t("createCampaignWizard.per1000ViewsSuffix")}</span>
                      </div>
                    )}
                    {formData.platforms.instagramReels && (
                      <div className="flex items-center gap-3">
                        <FaInstagram className="h-5 w-5 flex-shrink-0" />
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={formData.perPlatformRates.instagramReels}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            perPlatformRates: { ...prev.perPlatformRates, instagramReels: e.target.value }
                          }))}
                          placeholder="0.1"
                          className={`flex-1 rounded-radius-md px-4 py-3 text-base outline-none transition ${
                            errors.perPlatformRates?.instagramReels ? inputError : inputBase
                          }`}
                        />
                        <span className={`text-subheadline ${mutedText} whitespace-nowrap`}>{t("createCampaignWizard.per1000ViewsSuffix")}</span>
                      </div>
                    )}
                    {formData.platforms.facebookReels && (
                      <div className="flex items-center gap-3">
                        <FaFacebookF className="h-5 w-5 flex-shrink-0" />
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={formData.perPlatformRates.facebookReels}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            perPlatformRates: { ...prev.perPlatformRates, facebookReels: e.target.value }
                          }))}
                          placeholder="0.1"
                          className={`flex-1 rounded-radius-md px-4 py-3 text-base outline-none transition ${
                            errors.perPlatformRates?.facebookReels ? inputError : inputBase
                          }`}
                        />
                        <span className={`text-subheadline ${mutedText} whitespace-nowrap`}>{t("createCampaignWizard.per1000ViewsSuffix")}</span>
                      </div>
                    )}
                    {formData.platforms.youtubeShorts && (
                      <div className="flex items-center gap-3">
                        <FaYoutube className="h-5 w-5 flex-shrink-0" />
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={formData.perPlatformRates.youtubeShorts}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            perPlatformRates: { ...prev.perPlatformRates, youtubeShorts: e.target.value }
                          }))}
                          placeholder="0.1"
                          className={`flex-1 rounded-radius-md px-4 py-3 text-base outline-none transition ${
                            errors.perPlatformRates?.youtubeShorts ? inputError : inputBase
                          }`}
                        />
                        <span className={`text-subheadline ${mutedText} whitespace-nowrap`}>{t("createCampaignWizard.per1000ViewsSuffix")}</span>
                      </div>
                    )}
                    {formData.platforms.youtubeLongForm && (
                      <div className="flex items-center gap-3">
                        <FaYoutube className="h-5 w-5 flex-shrink-0" />
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={formData.perPlatformRates.youtubeLongForm}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            perPlatformRates: { ...prev.perPlatformRates, youtubeLongForm: e.target.value }
                          }))}
                          placeholder="0.1"
                          className={`flex-1 rounded-radius-md px-4 py-3 text-base outline-none transition ${
                            errors.perPlatformRates?.youtubeLongForm ? inputError : inputBase
                          }`}
                        />
                        <span className={`text-subheadline ${mutedText} whitespace-nowrap`}>{t("createCampaignWizard.per1000ViewsSuffix")}</span>
                      </div>
                    )}
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Step 3: Additional Assets & links */}
        <div style={getSlideStyle(3)}>
          <div className="space-y-6">
            <div className="space-y-6">
              <div>
                <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                  <span className="text-headline text-label-primary">{t("campaigns.additionalAssets")}</span>
                  <p className={`text-subheadline ${mutedText}`}>
                    Upload as many files as you need (stored securely). Add reference links below.
                  </p>
                  {(formData.existingAttachmentUrls || []).map((url, idx) => (
                    <div
                      key={`ex-${idx}`}
                      className={`flex items-center justify-between gap-2 rounded-radius-md border px-4 py-3 text-subheadline ${
                        "border-separator-default bg-bg-raised"
                      }`}
                    >
                      <a href={url} target="_blank" rel="noopener noreferrer" className={`truncate font-medium ${baseText}`}>
                        {url}
                      </a>
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingUrl(idx)}
                        className={`flex-shrink-0 p-2 rounded-radius-xs hover:bg-fill-quaternary`}
                        aria-label="Remove"
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {(formData.additionalAssetFiles || []).map((file, idx) => (
                    <div
                      key={`new-${idx}`}
                      className={`flex items-center justify-between gap-2 rounded-radius-md border px-4 py-3 ${
                        "border-separator-default bg-bg-raised"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <FiUpload className={`h-5 w-5 flex-shrink-0 ${baseText}`} />
                        <div className="min-w-0">
                          <p className={`text-subheadline font-medium truncate ${baseText}`}>{file.name}</p>
                          <p className={`text-footnote ${mutedText}`}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePendingFile(idx)}
                        className={`p-2 rounded-radius-xs hover:bg-fill-quaternary`}
                      >
                        <FiX className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <label
                    className={`flex flex-col items-center justify-center w-full min-h-[140px] rounded-radius-md border-2 border-dashed cursor-pointer transition ${
                      "border-separator-opaque bg-fill-quaternary"
                    }`}
                  >
                    <input
                      type="file"
                      ref={additionalAssetsFileInputRef}
                      onChange={handleAdditionalFileChange}
                      className="hidden"
                      multiple
                    />
                    <FiUpload className={`h-8 w-8 mb-2 ${mutedText}`} />
                    <span className={`text-subheadline font-medium ${baseText}`}>Add files</span>
                    <span className={`text-footnote ${mutedText}`}>Multiple files — up to 50 MB each</span>
                  </label>
                </label>
              </div>
              <div>
                <p className={`text-subheadline-semibold ${baseText} mb-2`}>Resource links</p>
                <p className={`text-footnote ${mutedText} mb-3`}>Add any number of URLs (Drive folders, Figma, docs, etc.)</p>
                <div className="space-y-2">
                  {(formData.resourceLinks || [""]).map((link, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="url"
                        value={link}
                        onChange={(e) => {
                          const next = [...(formData.resourceLinks || [""])];
                          next[idx] = e.target.value;
                          setFormData((prev) => ({ ...prev, resourceLinks: next }));
                        }}
                        placeholder="https://"
                        className={`flex-1 rounded-radius-md px-4 py-3 text-subheadline outline-none transition ${inputBase}`}
                      />
                      {(formData.resourceLinks || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = (formData.resourceLinks || []).filter((_, i) => i !== idx);
                            setFormData((prev) => ({ ...prev, resourceLinks: next.length ? next : [""] }));
                          }}
                          className={`px-3 rounded-radius-md ${buttonBase}`}
                        >
                          <FiX className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, resourceLinks: [...(prev.resourceLinks || [""]), ""] }))}
                    className={`text-subheadline-semibold text-accent-text`}
                  >
                    + Add link
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 4: Duration */}
        <div style={getSlideStyle(4)}>
          <div className="space-y-6">
            <div>
              <label className={`flex flex-col gap-3 text-subheadline-semibold ${baseText}`}>
                <span className="text-headline text-label-primary">{t("campaigns.campaignDuration")}</span>
                <div className="space-y-4">
                  <div>
                    <label className={`text-subheadline ${mutedText} mb-2 block`}>{t("campaigns.startDate")}</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className={`w-full rounded-radius-md px-4 py-3 text-base outline-none transition ${
                        errors.startDate ? inputError : inputBase
                      }`}
                    />
                    {errors.startDate && (
                      <p className={`text-subheadline ${errorText}`}>{errors.startDate}</p>
                    )}
                  </div>

                  <div>
                    <label className={`text-subheadline ${mutedText} mb-2 block`}>{t("campaigns.endDate")}</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      disabled={formData.runsUntilBudgetSpent}
                      className={`w-full rounded-radius-md px-4 py-3 text-base outline-none transition ${
                        formData.runsUntilBudgetSpent 
                          ? "opacity-50 cursor-not-allowed" 
                          : ""
                      } ${
                        errors.endDate ? inputError : inputBase
                      }`}
                    />
                    {errors.endDate && (
                      <p className={`text-subheadline ${errorText}`}>{errors.endDate}</p>
                    )}
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, runsUntilBudgetSpent: !prev.runsUntilBudgetSpent }))}
                      className={`w-full flex items-center gap-3 rounded-radius-md border-2 px-4 py-3 text-left transition ${
                        formData.runsUntilBudgetSpent
                          ? "border-accent-default bg-accent-tinted"
                          : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        formData.runsUntilBudgetSpent 
                          ? "border-accent-default bg-accent-default" 
                          : "border-separator-opaque"
                      }`}>
                        {formData.runsUntilBudgetSpent && (
                          <svg className="w-3 h-3 text-accent-on" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={baseText}>{t("campaigns.runsUntilBudgetSpent")}</span>
                    </button>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Step 5: Post to Announcements */}
        <div style={getSlideStyle(5)}>
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className={`text-title-1 mb-2 ${baseText}`}>
                {t("createCampaignWizard.shareYourCampaign")}
              </h3>
              <p className={`text-subheadline ${mutedText}`}>
                {t("createCampaignWizard.shareYourCampaignSubtitle")}
              </p>
            </div>
            
            <div>
              <label className={`flex flex-col gap-4 text-subheadline-semibold ${baseText}`}>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, postToAnnouncements: !prev.postToAnnouncements }))}
                  className={`w-full flex items-center gap-4 rounded-radius-md border-2 px-6 py-4 text-left transition ${
                    formData.postToAnnouncements
                      ? "border-accent-default bg-accent-tinted"
                      : `border-separator-default bg-bg-raised hover:bg-fill-quaternary`
                  }`}
                >
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    formData.postToAnnouncements 
                      ? "border-accent-default bg-accent-default" 
                      : "border-separator-opaque"
                  }`}>
                    {formData.postToAnnouncements && (
                      <svg className="w-4 h-4 text-accent-on" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className={`text-title-3 block mb-1 ${baseText}`}>
                      {t("createCampaignWizard.postToAnnouncementsTitle")}
                    </span>
                    <span className={`text-subheadline ${mutedText}`}>
                      {t("createCampaignWizard.postToAnnouncementsBody")}
                    </span>
                  </div>
                </button>
              </label>
            </div>
          </div>
        </div>

        </div>

        {/* Navigation Buttons */}
        <div
          className={`mt-4 flex shrink-0 items-center justify-between gap-3 border-t pt-4 ${
            "border-separator-default"
          }`}
        >
          <Button variant="gray" size="m" onClick={handlePrev} disabled={step === 1}>
            {t("common.previous")}
          </Button>
          <Button
            type={isLastStep ? "submit" : "button"}
            size="m"
            onClick={isLastStep ? undefined : handleNext}
            loading={isSubmitting}
            className="!bg-app-creator-rewards-base !text-app-creator-rewards-on"
          >
            {isSubmitting
              ? isEditMode
                ? t("createCampaignWizard.updating")
                : t("createCampaignWizard.creating")
              : isLastStep
              ? isEditMode
                ? t("campaigns.updateCampaign")
                : t("createCampaignWizard.launchCampaign")
              : t("common.next")}
          </Button>
        </div>
      </form>
        </div>

        {/* Live preview */}
        <aside className="hidden min-h-0 lg:block">
          <div className="sticky top-0 overflow-hidden rounded-radius-lg bg-bg-raised p-space-16">
            <p className="mb-space-4 text-caps-label uppercase text-accent-text">
              {t("campaigns.livePreview")}
            </p>
            <p className={`relative mb-4 text-caption-1 ${mutedText}`}>
              {t("createCampaignWizard.previewSubtitle")}
            </p>
            <CampaignPreview />
          </div>
        </aside>
      </div>

      <Modal
        open={showConfirmationModal}
        onClose={() => !isSubmitting && setShowConfirmationModal(false)}
        title={t("createCampaignWizard.importantInformation")}
        closeLabel={t("common.close")}
        width="min-[600px]:w-[560px]"
        isDark={isDark}
      >
        <div className="flex flex-col gap-space-8 rounded-[22px] bg-bg-inset p-space-16 text-subheadline text-label-secondary">
          <p className="text-subheadline-semibold text-label-primary">{t("createCampaignWizard.editRulesP1")}</p>
          <p>{t("createCampaignWizard.editRulesP2")}</p>
          <p>{t("createCampaignWizard.editRulesP3")}</p>
          <p>{t("createCampaignWizard.editRulesP4")}</p>
          <p>{t("createCampaignWizard.editRulesP5")}</p>
          <p className="mt-space-8 text-subheadline-semibold text-label-primary">{t("createCampaignWizard.editRulesConfirm")}</p>
        </div>
        <div className="flex justify-end gap-space-12">
          <Button variant="gray" disabled={isSubmitting} onClick={() => setShowConfirmationModal(false)}>
            {t("common.back")}
          </Button>
          <Button className={isSubmitting ? "" : "!bg-app-creator-rewards-base !text-app-creator-rewards-on"} loading={isSubmitting} onClick={handleConfirmCreate}>
            {isSubmitting ? t("createCampaignWizard.creating") : t("common.confirm")}
          </Button>
        </div>
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate("/");
        }}
        title={t("createCampaignWizard.campaignCreatedTitle")}
        description={t("createCampaignWizard.campaignCreatedP1")}
        closeLabel={t("common.close")}
        isDark={isDark}
      >
        <p className="text-subheadline text-label-secondary">{t("createCampaignWizard.campaignCreatedP2")}</p>
        <div className="flex justify-end">
          <Button
            size="m"
            className="!bg-app-creator-rewards-base !text-app-creator-rewards-on"
            onClick={() => {
              setShowSuccessModal(false);
              navigate("/");
            }}
          >
            {t("common.ok")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CreateCampaignPage;

